import API_BASE from '../../config';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = API_BASE;

// ── Color palette ─────────────────────────────────────────────────────────────
const C = {
  primary: '#1A6FD6',
  navy: '#0D1F3C',
  navyLight: '#1E3A5F',
  bg: '#FFFBEB',
  border: '#E5E9F0',
  body: '#6B7280',
  white: '#ffffff',
  sidebar: '#0F172A',
  sidebarHover: '#1E293B',
  sidebarActive: '#1E3A5F',
  sidebarText: '#94A3B8',
  sidebarTextActive: '#ffffff',
};

// ── Stage configuration ───────────────────────────────────────────────────────
const STAGE_CONFIG = {
  intake_done: { label: 'Intake Done', bg: '#FEF9C3', color: '#854D0E', border: '#FDE68A' },
  recruiter_assigned: { label: 'Assigned', bg: '#DBEAFE', color: '#1E40AF', border: '#BFDBFE' },
  expert_qa: { label: 'Expert QA', bg: '#EDE9FE', color: '#5B21B6', border: '#DDD6FE' },
  delivered: { label: 'Delivered', bg: '#DCFCE7', color: '#14532D', border: '#BBF7D0' },
  new: { label: 'New', bg: '#FEF9C3', color: '#854D0E', border: '#FDE68A' },
  intake_pending: { label: 'Intake Pending', bg: '#F3F4F6', color: '#374151', border: '#E5E7EB' },
  ai_rewrite: { label: 'AI Rewrite', bg: '#E0F2FE', color: '#0C4A6E', border: '#BAE6FD' },
  expert_review: { label: 'Expert Review', bg: '#EDE9FE', color: '#5B21B6', border: '#DDD6FE' },
  final_fix: { label: 'Final Fix', bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' },
  recruiter_cert: { label: 'Recruiter Cert', bg: '#F0FDF4', color: '#166534', border: '#BBF7D0' },
};

const getStageCfg = (stage) =>
  STAGE_CONFIG[stage] || { label: stage || 'Unknown', bg: '#F3F4F6', color: '#374151', border: '#E5E7EB' };

// ── Inline styles ─────────────────────────────────────────────────────────────
const s = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif',
    background: C.bg,
  },
  sidebar: {
    width: '220px',
    flexShrink: 0,
    background: C.sidebar,
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 50,
    overflowY: 'auto',
  },
  sidebarBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '24px 20px 8px',
  },
  brandDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: C.primary,
    flexShrink: 0,
  },
  brandName: {
    color: '#fff',
    fontWeight: '700',
    fontSize: '16px',
    letterSpacing: '-0.3px',
  },
  sidebarSub: {
    fontSize: '10px',
    fontWeight: '600',
    color: '#475569',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    padding: '0 20px 20px',
  },
  navSection: {
    padding: '0 12px',
    flex: 1,
  },
  navItem: (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 12px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: active ? '600' : '500',
    color: active ? C.sidebarTextActive : C.sidebarText,
    background: active ? C.sidebarActive : 'transparent',
    cursor: 'pointer',
    marginBottom: '2px',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    textDecoration: 'none',
    transition: 'background 0.1s, color 0.1s',
  }),
  sidebarFooter: {
    padding: '16px 16px 20px',
    borderTop: '1px solid #1E293B',
  },
  recruiterInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  avatar: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    background: C.primary,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '13px',
    flexShrink: 0,
  },
  recruiterName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#E2E8F0',
    lineHeight: 1.2,
  },
  recruiterCompany: {
    fontSize: '11px',
    color: '#64748B',
    lineHeight: 1.2,
    marginTop: '1px',
  },
  signOutBtn: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: '8px',
    background: '#1E293B',
    border: 'none',
    color: '#94A3B8',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background 0.1s, color 0.1s',
  },
  main: {
    marginLeft: '220px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  header: {
    background: '#fff',
    borderBottom: `1px solid ${C.border}`,
    padding: '20px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: C.navy,
    margin: 0,
    lineHeight: 1.2,
  },
  headerSub: {
    fontSize: '13px',
    color: C.body,
    marginTop: '2px',
  },
  content: {
    padding: '28px 32px',
    flex: 1,
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '28px',
  },
  statCard: {
    background: '#fff',
    borderRadius: '12px',
    border: `1px solid ${C.border}`,
    padding: '20px 24px',
  },
  statLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: C.body,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '8px',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: C.navy,
    lineHeight: 1,
  },
  statSub: {
    fontSize: '12px',
    color: C.body,
    marginTop: '4px',
  },
  tableCard: {
    background: '#fff',
    borderRadius: '12px',
    border: `1px solid ${C.border}`,
    overflow: 'hidden',
  },
  tableHeader: {
    padding: '16px 20px',
    borderBottom: `1px solid ${C.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tableTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: C.navy,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '10px 16px',
    fontSize: '11px',
    fontWeight: '600',
    color: C.body,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    textAlign: 'left',
    background: '#FFFBEB',
    borderBottom: `1px solid ${C.border}`,
  },
  td: {
    padding: '14px 16px',
    fontSize: '13.5px',
    color: C.navy,
    borderBottom: `1px solid ${C.border}`,
    verticalAlign: 'middle',
  },
  tdMuted: {
    padding: '14px 16px',
    fontSize: '13px',
    color: C.body,
    borderBottom: `1px solid ${C.border}`,
    verticalAlign: 'middle',
  },
  actionBtn: {
    padding: '6px 14px',
    borderRadius: '7px',
    background: '#EFF6FF',
    border: `1px solid #BFDBFE`,
    color: C.primary,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.12s',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 24px',
    gap: '12px',
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: C.navy,
  },
  emptyText: {
    fontSize: '14px',
    color: C.body,
    textAlign: 'center',
    maxWidth: '280px',
  },
  loadingWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    flexDirection: 'column',
    gap: '12px',
  },
  spinner: {
    width: '28px',
    height: '28px',
    border: `3px solid ${C.border}`,
    borderTopColor: C.primary,
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
};

// ── Helper: format date ───────────────────────────────────────────────────────
const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Stage badge ───────────────────────────────────────────────────────────────
const StageBadge = ({ stage }) => {
  const cfg = getStageCfg(stage);
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      background: cfg.bg,
      color: cfg.color,
      border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
};

// ── Nav icon SVGs ─────────────────────────────────────────────────────────────
const OrdersIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
    <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5 5h6M5 8h6M5 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ProfileIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
    <circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2 14c0-2.8 2.7-5 6-5s6 2.2 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SignOutIcon = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 16 16">
    <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3m0 0l-3-3m3 3H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Order List Section (Greenlight-style) ────────────────────────────────────
const OrderListSection = ({ title, subtitle, deliveredCount, orders, emptyMessage, navigate, isCompleted }) => (
  <div style={{ ...s.tableCard, marginBottom: 24 }}>
    <div style={{ ...s.tableHeader, flexWrap: 'wrap', gap: 8 }}>
      <div>
        <div style={{ ...s.tableTitle, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: '12px', color: C.body }}>{subtitle}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
        <span style={{ fontSize: '12px', color: C.body }}>delivered</span>
        <span style={{
          minWidth: '28px', height: '28px', borderRadius: '50%',
          background: isCompleted ? '#DCFCE7' : '#DBEAFE',
          color: isCompleted ? '#14532D' : C.primary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: '700', fontSize: '13px',
        }}>{deliveredCount}</span>
      </div>
    </div>

    {orders.length === 0 ? (
      <div style={{ padding: '32px', textAlign: 'center', color: C.body, fontSize: '13px' }}>{emptyMessage}</div>
    ) : (
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              {['Order ID', 'Candidate', 'Industry', 'Order Start', 'Completed', 'Hours Taken', 'Status'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const hoursStr = (() => {
                if (!order.created_at || !order.delivered_at) return '—';
                const h = (new Date(order.delivered_at) - new Date(order.created_at)) / 3600000;
                return `${h.toFixed(1)}h`;
              })();
              const industry = order.target_industry || order.current_industry || '—';
              const industryColors = {
                'Manufacturing': '#F59E0B', 'Banking & Finance': '#3B82F6', 'Banking / BFSI': '#3B82F6',
                'Tech & Product': '#8B5CF6', 'Technology': '#8B5CF6', 'Consulting': '#06B6D4',
                'FMCG': '#10B981', 'Pharma': '#EC4899', 'FinTech': '#6366F1',
              };
              const indColor = industryColors[industry] || C.primary;
              return (
                <tr key={order.id} style={{ transition: 'background 0.1s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#FFFBEB'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <td style={s.td}>
                    <span style={{ fontFamily: 'monospace', fontWeight: '600', fontSize: '12px', color: C.navy }}>
                      {order.order_id || order.id?.slice(0, 8) || '—'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <span style={{ fontWeight: '500' }}>{order.customer_name || order.user_name || '—'}</span>
                  </td>
                  <td style={s.td}>
                    <span style={{ color: indColor, fontWeight: '500', fontSize: '13px' }}>{industry}</span>
                  </td>
                  <td style={s.tdMuted}>{formatDate(order.created_at)}</td>
                  <td style={s.tdMuted}>{order.delivered_at ? formatDate(order.delivered_at) : '—'}</td>
                  <td style={{ ...s.td, fontWeight: '700', color: hoursStr !== '—' ? '#0F172A' : C.body }}>
                    {hoursStr}
                  </td>
                  <td style={s.td}><StageBadge stage={order.stage} /></td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    <button style={s.actionBtn}
                      onClick={() => navigate(`/recruiter/orders/${order.order_id || order.id}`)}
                      onMouseEnter={e => { e.currentTarget.style.background = '#DBEAFE'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#EFF6FF'; }}>
                      View →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const RecruiterDashboard = () => {
  const navigate = useNavigate();
  const [recruiter, setRecruiter] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Auth guard
  useEffect(() => {
    if (sessionStorage.getItem('recruiter_auth') !== '1') {
      navigate('/recruiter/login');
    }
  }, [navigate]);

  const handleUnauthorized = useCallback(() => {
    sessionStorage.removeItem('recruiter_auth');
    navigate('/recruiter/login');
  }, [navigate]);

  const fetchMe = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/recruiter-portal/me`, {
      credentials: 'include',
    });
    if (res.status === 401) { handleUnauthorized(); return; }
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to load profile');
    setRecruiter(data);
  }, [handleUnauthorized]);

  const fetchOrders = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/recruiter-portal/orders`, {
      credentials: 'include',
    });
    if (res.status === 401) { handleUnauthorized(); return; }
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to load orders');
    setOrders(data.orders || []);
  }, [handleUnauthorized]);

  useEffect(() => {
    if (sessionStorage.getItem('recruiter_auth') !== '1') return;
    setLoading(true);
    Promise.all([fetchMe(), fetchOrders()])
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [fetchMe, fetchOrders]);

  const handleSignOut = async () => {
    try {
      await fetch(`${API_URL}/api/recruiter-portal/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_) { /* ignore */ }
    sessionStorage.removeItem('recruiter_auth');
    navigate('/recruiter/login');
  };

  // Derived stats
  const totalAssigned = orders.length;
  const completed = orders.filter((o) => o.stage === 'delivered').length;
  const assignedOrders = orders.filter((o) => o.stage !== 'delivered');
  const completedOrders = orders.filter((o) => o.stage === 'delivered');

  const avgHours = (() => {
    const withTime = orders.filter(o => o.created_at && o.delivered_at);
    if (!withTime.length) return null;
    const avg = withTime.reduce((s, o) => s + (new Date(o.delivered_at) - new Date(o.created_at)), 0) / withTime.length / 3600000;
    return avg.toFixed(1);
  })();

  if (loading) {
    return (
      <>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={s.loadingWrap}>
          <div style={s.spinner} />
          <span style={{ fontSize: '14px', color: C.body }}>Loading your dashboard…</span>
        </div>
      </>
    );
  }

  const initials = recruiter?.name
    ? recruiter.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'RC';

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        body { margin: 0; }
        tr:last-child td { border-bottom: none !important; }
      `}</style>

      <div style={s.layout}>
        {/* ── Sidebar ── */}
        <aside style={s.sidebar}>
          <div style={s.sidebarBrand}>
            <div style={s.brandDot} />
            <span style={s.brandName}>Successlane</span>
          </div>
          <div style={s.sidebarSub}>Recruiter Portal</div>

          <nav style={s.navSection}>
            <button style={s.navItem(true)} onClick={() => {}}>
              <OrdersIcon />
              My Orders
            </button>
            <button
              style={{ ...s.navItem(false), opacity: 0.5, cursor: 'default' }}
              disabled
              title="Coming soon"
            >
              <ProfileIcon />
              Profile
            </button>
          </nav>

          <div style={s.sidebarFooter}>
            <div style={s.recruiterInfo}>
              <div style={s.avatar}>{initials}</div>
              <div style={{ minWidth: 0 }}>
                <div style={s.recruiterName} title={recruiter?.name}>
                  {recruiter?.name || 'Recruiter'}
                </div>
                <div style={s.recruiterCompany} title={recruiter?.company}>
                  {recruiter?.company || ''}
                </div>
              </div>
            </div>
            <button
              style={s.signOutBtn}
              onClick={handleSignOut}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#334155'; e.currentTarget.style.color = '#E2E8F0'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#1E293B'; e.currentTarget.style.color = '#94A3B8'; }}
            >
              <SignOutIcon />
              Sign out
            </button>
          </div>
        </aside>

        {/* ── Main ── */}
        <main style={s.main}>
          {/* Header */}
          <div style={s.header}>
            <div>
              <h1 style={s.headerTitle}>
                {recruiter?.email || recruiter?.name || 'My Dashboard'}
              </h1>
              <div style={{ ...s.headerSub, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                <span>{totalAssigned} assigned</span>
                <span style={{ color: '#D1D5DB' }}>·</span>
                <span style={{ color: '#16A34A', fontWeight: 600 }}>{completed} completed</span>
                {avgHours && (
                  <>
                    <span style={{ color: '#D1D5DB' }}>·</span>
                    <span>avg {avgHours}h per order</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div style={s.content}>
            {error && (
              <div style={{ padding: '12px 16px', borderRadius: '10px', background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: '13.5px', marginBottom: '20px' }}>
                {error}
              </div>
            )}

            {/* Assigned Orders section */}
            <OrderListSection
              title={recruiter?.email || 'My Orders'}
              subtitle={`${totalAssigned} assigned · ${completed} completed${avgHours ? ` · avg ${avgHours}h per order` : ''}`}
              deliveredCount={completed}
              orders={assignedOrders}
              emptyMessage="No active orders right now."
              navigate={navigate}
            />

            {/* Completed Orders section */}
            {completedOrders.length > 0 && (
              <OrderListSection
                title="Completed"
                subtitle={`${completedOrders.length} delivered`}
                deliveredCount={completedOrders.length}
                orders={completedOrders}
                emptyMessage=""
                navigate={navigate}
                isCompleted
              />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default RecruiterDashboard;
